// Plan Limits Configuration for Freemium System
// This defines the limits for each plan type

export const PLAN_LIMITS = {
  free: {
    name: 'Free',
    maxContacts: 50,
    maxEmailsPerMonth: 100,
    maxCampaigns: 1,
    features: {
      basicAutomation: true,
      emailSupport: true,
      leadCaptureForms: true,
      teaserAI: true,
      // Disabled features for free plan
      advancedAI: false,
      premiumIntegrations: false,
      prioritySupport: false,
      customIntegrations: false,
      whiteLabel: false,
      multiTenant: false,
      analytics3D: false,
      voiceControl: false
    }
  },
  
  pro: {
    name: 'Professional',
    price: 29, // $29/month
    yearlyPrice: 261, // $261/year (25% discount from $348)
    maxContacts: 10000,
    maxEmailsPerMonth: 50000,
    maxCampaigns: -1, // -1 means unlimited
    features: {
      basicAutomation: true,
      emailSupport: true,
      leadCaptureForms: true,
      basicAI: true,
      advancedAI: true,
      premiumIntegrations: true,
      prioritySupport: true,
      customIntegrations: true,
      analytics3D: true,
      voiceControl: true,
      // White label available as separate purchase (Coming Q2 2026: $4,997 Full License / $1,497 Revenue Share)
      whiteLabel: false, // Requires separate white label license purchase
      whiteLabelEligible: true, // Can purchase white label license
      multiTenant: false
    }
  },
  
  pro_monthly: {
    name: 'Professional',
    price: 29, // $29/month
    maxContacts: 10000,
    maxEmailsPerMonth: 50000,
    maxCampaigns: -1,
    features: {
      basicAutomation: true,
      emailSupport: true,
      leadCaptureForms: true,
      basicAI: true,
      advancedAI: true,
      premiumIntegrations: true,
      prioritySupport: true,
      customIntegrations: true,
      analytics3D: true,
      voiceControl: true,
      whiteLabel: false,
      whiteLabelEligible: true,
      multiTenant: false
    }
  },

  pro_yearly: {
    name: 'Professional (Yearly)',
    price: 261, // $261/year
    maxContacts: 10000,
    maxEmailsPerMonth: 50000,
    maxCampaigns: -1,
    features: {
      basicAutomation: true,
      emailSupport: true,
      leadCaptureForms: true,
      basicAI: true,
      advancedAI: true,
      premiumIntegrations: true,
      prioritySupport: true,
      customIntegrations: true,
      analytics3D: true,
      voiceControl: true,
      whiteLabel: false,
      whiteLabelEligible: true,
      multiTenant: false
    }
  },

  whitelabel_yearly: {
    name: 'White Label Yearly',
    price: 497,
    maxContacts: 10000,
    maxEmailsPerMonth: 50000,
    maxCampaigns: -1,
    features: {
      basicAutomation: true,
      emailSupport: true,
      leadCaptureForms: true,
      basicAI: true,
      advancedAI: true,
      premiumIntegrations: true,
      prioritySupport: true,
      customIntegrations: true,
      analytics3D: true,
      voiceControl: true,
      whiteLabel: true,
      whiteLabelEligible: true,
      multiTenant: true,
      customDomains: true
    }
  },
  
  lifetime: {
    name: 'Lifetime',
    maxContacts: -1, // unlimited
    maxEmailsPerMonth: -1, // unlimited
    maxCampaigns: -1, // unlimited
    features: {
      basicAutomation: true,
      emailSupport: true,
      leadCaptureForms: true,
      basicAI: true,
      advancedAI: true,
      premiumIntegrations: true,
      prioritySupport: true,
      customIntegrations: true,
      analytics3D: true,
      voiceControl: true,
      whiteLabel: false, // Requires separate white label license purchase
      whiteLabelEligible: true, // Can purchase white label license
      multiTenant: true
    }
  },

  // Lifetime plan with active White Label license
  lifetime_with_whitelabel: {
    name: 'Lifetime + White Label',
    maxContacts: -1, // unlimited
    maxEmailsPerMonth: -1, // unlimited
    maxCampaigns: -1, // unlimited
    features: {
      basicAutomation: true,
      emailSupport: true,
      leadCaptureForms: true,
      basicAI: true,
      advancedAI: true,
      premiumIntegrations: true,
      prioritySupport: true,
      customIntegrations: true,
      analytics3D: true,
      voiceControl: true,
      whiteLabel: true,
      multiTenant: true
    }
  },
  
  // Existing users get founder benefits
  founder: {
    name: 'Founder',
    maxContacts: -1, // unlimited
    maxEmailsPerMonth: -1, // unlimited
    maxCampaigns: -1, // unlimited
    features: {
      basicAutomation: true,
      emailSupport: true,
      leadCaptureForms: true,
      basicAI: true,
      advancedAI: true,
      premiumIntegrations: true,
      prioritySupport: true,
      customIntegrations: true,
      analytics3D: true,
      voiceControl: true,
      whiteLabel: true,
      multiTenant: true
    }
  }
}

// White Label License Pricing
export const WHITE_LABEL_PRICING = {
  fullLicense: {
    price: 2997,
    name: 'White Label Full License',
    description: 'One-time payment. Keep 100% of revenue from your customers.',
    features: [
      'Complete rebranding rights',
      'Custom domain support',
      'Your logo and colors',
      'Keep 100% of customer revenue',
      'Unlimited sub-accounts',
      'Priority support',
      'Dedicated onboarding'
    ],
    requiresPlan: ['pro', 'pro_yearly', 'lifetime', 'founder']
  },
  revenueShare: {
    price: 997,
    name: 'White Label Revenue Share',
    description: 'Lower upfront cost. We receive 10% of your customer revenue.',
    revenueSharePercent: 10,
    features: [
      'Complete rebranding rights',
      'Custom domain support', 
      'Your logo and colors',
      '10% revenue share to Market Genie',
      'Unlimited sub-accounts',
      'Priority support',
      'Dedicated onboarding'
    ],
    requiresPlan: ['pro', 'pro_yearly', 'lifetime', 'founder']
  }
}

// Helper functions
const PLAN_ALIASES = {
  professional: 'pro',
  Professional: 'pro',
  agency: 'whitelabel_yearly',
  Agency: 'whitelabel_yearly'
}

export const getPlanLimits = (planType) => {
  const resolvedPlan = PLAN_ALIASES[planType] || PLAN_ALIASES[(planType || '').toString().toLowerCase()] || planType
  return PLAN_LIMITS[resolvedPlan] || PLAN_LIMITS.free
}

export const isFeatureEnabled = (planType, feature) => {
  const limits = getPlanLimits(planType)
  return limits.features[feature] || false
}

export const hasUnlimitedAccess = (planType) => {
  return planType === 'founder' || planType === 'lifetime'
}

export const isLimitReached = (planType, limitType, currentUsage) => {
  const limits = getPlanLimits(planType)
  const maxAllowed = limits[limitType]
  
  // -1 means unlimited
  if (maxAllowed === -1) return false
  
  return currentUsage >= maxAllowed
}

export const getLimitProgress = (planType, limitType, currentUsage) => {
  const limits = getPlanLimits(planType)
  const maxAllowed = limits[limitType]
  
  if (maxAllowed === -1) return 0 // unlimited
  
  return Math.min((currentUsage / maxAllowed) * 100, 100)
}

export const getRemainingQuota = (planType, limitType, currentUsage) => {
  const limits = getPlanLimits(planType)
  const maxAllowed = limits[limitType]
  
  if (maxAllowed === -1) return Infinity
  
  return Math.max(0, maxAllowed - currentUsage)
}